import { shallowEqual, useSelector } from "react-redux";
import { getProduct, selectIsIAM, selectSession } from "../app/sessionSlice";
import {
    AvataxRole,
    certificateExemptionManagementTag,
    EcmRole,
    retailTag,
    Subscription,
    TechnicalSupportAdminRole,
    TechnicalSupportUserRole,
} from "../features/navigation/Subscription";

import { retailPermissionsTag } from "../features/navigation/Permissions";
import { matchSubscriptions } from "../shared/Utils";
import type { Feature } from "./types";
import { useAccessible } from "./useAccessible";

// Interface for session object to avoid using 'any'
interface SessionData {
    isInternalUser: boolean;
    ecmRole?: EcmRole[];
    subscriptions?: string[];
    role?: string;
    transformedGrants?: Record<string, boolean>;
}

/**
 * Custom hook to check if the current user is authenticated via IAM
 * @returns {boolean} True if the user is authenticated via IAM, false otherwise
 */
export const useIsIAM = () => Boolean(useSelector(selectIsIAM));

/**
 * Custom hook to check if the current user is an internal user
 * @returns {boolean} True if the user is an internal user, false otherwise
 */
export const useIsInternalUser = () => {
    const { isInternalUser } = useSelector(selectSession, shallowEqual) as SessionData;
    return isInternalUser;
};

/**
 * Custom hook to check if the current user is a Technical Support User (TSU).
 * TSU is an internal Avalara staff role (session.role), distinct from the
 * per-company EcmRole/AvataxRole values.
 * @returns {boolean} True if the current session's role is Technical Support User
 */
export const useIsTechnicalSupportUser = (): boolean => {
    const session = useSelector(selectSession, shallowEqual) as SessionData;
    return session.role === TechnicalSupportUserRole;
};

/**
 * Custom hook to check if the current user is a Technical Support Admin (TSA).
 * TSA is an internal Avalara staff role (session.role), distinct from the
 * per-company EcmRole/AvataxRole values.
 * @returns {boolean} True if the current session's role is Technical Support Admin
 */
export const useIsTechnicalSupportAdmin = (): boolean => {
    const session = useSelector(selectSession, shallowEqual) as SessionData;
    return session.role === TechnicalSupportAdminRole;
};

/**
 * Custom hook to determine if the current user is a Managed Service User
 * Checks if the user's ECM role includes the Managed Service User role
 * @returns {boolean} True if the user is a Managed Service User, false otherwise
 */
export const useIsManagedServiceUser = (): boolean => {
    const session = useSelector(selectSession, shallowEqual) as SessionData;
    const isIAM = useIsIAM();
    return !isIAM && Boolean(session?.ecmRole?.includes(EcmRole.MSUser));
};

/**
 * Custom hook to validate if the user has access to a specific feature based on their subscriptions
 * Access is granted if:
 * 1. The feature is available to all subscriptions, or
 * 2. The user is a Managed Service User and the feature allows MSUser access, or
 * 3. The user's subscriptions match the feature's required subscriptions
 * @param {Feature} feature - The feature to validate access for
 * @returns {boolean} True if the user has access to the feature, false otherwise
 */
export const useValidateSubscriptions = (feature: Feature): boolean => {
    const session = useSelector(selectSession, shallowEqual) as SessionData;
    const isManagedServiceUser = useIsManagedServiceUser();

    // Handle subscription property - exists in FeatureFlagConfig but not AccessConfig
    const featureSubscription = "subscription" in feature ? feature.subscription : undefined;
    const featureEcmRole = "ecmRole" in feature ? feature.ecmRole : undefined;

    return (
        featureSubscription?.[0] === "All" ||
        (isManagedServiceUser && Boolean(featureEcmRole?.includes(EcmRole.MSUser))) ||
        matchSubscriptions(featureSubscription, session?.subscriptions)
    );
};

/**
 * Custom hook to check if the current user has a Certificate Exemption Management role then it has Exemption roles
 * Determines if any of the user's ECM roles start with the CEM tag
 * @returns {boolean} True if the user has a CEM role, false otherwise
 */
export const useIsCEMRole = (): boolean => {
    const session = useSelector(selectSession, shallowEqual) as SessionData;
    return Boolean(
        session.ecmRole?.filter((x: EcmRole) => x.startsWith(certificateExemptionManagementTag))
            .length
    );
};

/**
 * Custom hook to check if the current user has no access
 * @returns {boolean} True if the user has no access, false otherwise
 */
export const useIsNoAccessUser = (): boolean => {
    const session = useSelector(selectSession, shallowEqual) as SessionData;
    const isIAM = useIsIAM();
    if (!isIAM) return Boolean(session?.ecmRole?.includes(EcmRole.CEMNoAccess));
    // TODO(IAM): define no access account in IAM
    return false;
};

/**
 * Custom hook to check if the current user is a retail-only user
 * @returns {boolean} True if the user is a retail-only user, false otherwise
 */
export const useIsRetailOnlyUser = (): boolean => {
    const session = useSelector(selectSession, shallowEqual) as SessionData;
    const isIAM = useIsIAM();

    if (!isIAM)
        return (
            session.role === AvataxRole.ECMRoleUser &&
            Boolean(session.ecmRole?.find((x: EcmRole) => x?.startsWith(retailTag)))
        );
    return Object.keys(session.transformedGrants || {})?.every(key =>
        key?.startsWith(retailPermissionsTag)
    );
};

/**
 * Custom hook to check if the current user has a basic account
 * @returns {boolean} True if the user has a basic account, false if they have any premium subscription
 */
export const useIsBasicAccount = (): boolean => {
    const isIAM = useIsIAM();
    const subscriptions = useSelector(getProduct, shallowEqual) as string[];

    // TODO(IAM): define basic account in IAM
    if (isIAM) return false;
    if (!subscriptions?.length) return true;

    const premiumSubscriptions = [
        Subscription.ECMPremium,
        Subscription.ECMStandard,
        Subscription.ECMPro,
        Subscription.ECMEssentials,
        "AvaExemptTier1",
        "AvaExemptTier2",
        "AvaExemptTier3",
        "AvaExemptTier4",
        Subscription.VEPRO,
        Subscription.VEPREMIUM,
        Subscription.AVATAXAP,
    ];

    return !premiumSubscriptions.some(sub => subscriptions.includes(sub));
};

/**
 * Custom hook to check if a feature is eligible for the current user
 * @param {Feature} feature - The feature to check eligibility for
 * @param {boolean} viewOnly - Whether this is for view-only access
 * @returns {boolean} True if the feature is eligible, false otherwise
 */
export const useIsEligible = (feature: Feature, viewOnly: boolean): boolean => {
    const isValidated = useValidateSubscriptions(feature);
    const { hasIAMAccess, hasNonIAMAccess } = useAccessible();

    if (isValidated) {
        if (viewOnly) return true;
        return hasNonIAMAccess(feature) || hasIAMAccess(feature);
    }
    return false;
};

/**
 * Custom hook to validate if an internal user is required
 * @param {boolean} requireInternalUser - Whether an internal user is required
 * @returns {boolean} True if validation passes, false otherwise
 */
export const useInternalUserValidation = (requireInternalUser: boolean | null): boolean => {
    const { isInternalUser } = useSelector(selectSession, shallowEqual) as SessionData;
    if (requireInternalUser !== null) {
        // If requireInternalUser is true, only internal users should see it
        // If requireInternalUser is false, only non-internal users should see it
        return requireInternalUser === isInternalUser;
    }
    return true;
};

/**
 * Custom hook to check if the current user is an AvaTax user
 * @returns {boolean} True if the user is an AvaTax user, false otherwise
 */
export const useIsAvaTaxUser = (): boolean => {
    const session = useSelector(selectSession, shallowEqual) as SessionData;
    return Boolean(
        (session?.subscriptions?.includes(Subscription.AVATAXST) &&
            session?.subscriptions?.includes(Subscription.AVATAXPRO)) ||
        session?.subscriptions?.includes(Subscription.AVAGLOBAL) ||
        session?.subscriptions?.includes(Subscription.AVATAXGLOBAL)
    );
};
